import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getUrls } from '@shared/utils/config/environment';
import { useLocalStorage } from 'usehooks-ts';
export function formatChartDate(date, periodLabel) {
  switch (periodLabel) {
    case '1H':
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    case '1D':
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    case '1W':
    case '1M':
      return date.toLocaleString('default', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    case '3M':
    case '1Y':
    case 'ALL':
      return date.toLocaleString('default', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    default:
      return date.toLocaleString('default', {
        // month: 'short',
        // day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
  }
}
const _getUserWalletDataByTimeframe = async ({
  ethWallet,
  solanaWallet,
  currentResolution
}) => {
  let ethData, solData;
  try {
    ethData = await axios.get(`${getUrls().INDEXER_URL}/portfolio?wallet=${ethWallet}&timeFrame=${currentResolution.timeFrame}&doNotIncludeTokens=true`);
  } catch (error) {
    console.error('Error fetching Ethereum wallet data:', error);
    ethData = {
      data: {
        status: 'error',
        data: []
      }
    };
  }
  try {
    solData = await axios.get(`${getUrls().INDEXER_URL}/portfolio?wallet=${solanaWallet}&timeFrame=${currentResolution.timeFrame}&doNotIncludeTokens=true`);
  } catch (error) {
    console.error('Error fetching Solana wallet data:', error);
    solData = {
      data: {
        status: 'error',
        data: []
      }
    };
  }
  if (ethData.data.status === 'error' && solData.data.status === 'error') throw new Error('_getUserWalletDataByTimeframe:Could not fetch any data.');
  return {
    ethData,
    solData
  };
};
const _getBarData = (ethData, solData) => {
  let barData = [];
  if (ethData.data.status === 'success' && solData.data.status === 'success') {
    let index1 = 0,
      index2 = 0,
      prevEthUsdValue = 0,
      prevSolUsdValue = 0;
    while (index1 < ethData.data.totals.length || index2 < solData.data.totals.length) {
      let ethTimeStamp = 0,
        ethUsdValue = prevEthUsdValue,
        solTimeStamp = 0,
        solUsdValue = prevSolUsdValue;
      if (ethData.data.totals[index1]) {
        ethTimeStamp = ethData.data.totals[index1]['unix_timestamp'];
        ethUsdValue = ethData.data.totals[index1]['total'];
        prevEthUsdValue = ethUsdValue;
      }
      if (solData.data.totals[index2]) {
        solTimeStamp = solData.data.totals[index2]['unix_timestamp'];
        solUsdValue = solData.data.totals[index2]['total'];
        prevSolUsdValue = solUsdValue;
      }
      if (ethTimeStamp == solTimeStamp) {
        barData.push({
          value: ethUsdValue + solUsdValue,
          time: ethTimeStamp
        });
        index1++;
        index2++;
      } else if (ethTimeStamp < solTimeStamp || solTimeStamp == 0) {
        barData.push({
          value: ethUsdValue + solUsdValue,
          time: ethTimeStamp
        });
        index1++;
        if (index1 > ethData.data.totals.length) {
          break;
        }
      } else {
        barData.push({
          value: ethUsdValue + solUsdValue,
          time: solTimeStamp
        });
        index2++;
        if (index2 > solData.data.totals.length) {
          break;
        }
      }
    }
  }
  barData = barData.sort((a, b) => a.time - b.time).filter((item, index, array) => index === 0 || item.time !== array[index - 1].time);
  return barData;
};
export const usePortfolioChartData = ({
  ethWallet,
  solanaWallet,
  currentResolution
}) => {
  const [portfolioChartDataLC, setPortfolioChartDataLC] = useLocalStorage('portfolioChartData', {});
  const query = useQuery({
    queryKey: ['getPortfolioChartData', {
      ethWallet,
      solanaWallet,
      currentResolution
    }],
    refetchInterval: 5000,
    queryFn: async () => {
      var _a;
      const _params = {
        ethWallet,
        solanaWallet,
        currentResolution
      };
      const res = await _getUserWalletDataByTimeframe(Object.assign({}, _params));
      const barData = (_a = _getBarData(res.ethData, res.solData)) === null || _a === void 0 ? void 0 : _a.filter(item => item.time > 0);
      const newLCData = Object.assign({}, portfolioChartDataLC);
      newLCData[currentResolution.periodLabel] = barData;
      setPortfolioChartDataLC(newLCData);
      return barData === null || barData === void 0 ? void 0 : barData.filter(item => item.time > 0);
    },
    enabled: Boolean(ethWallet || solanaWallet) && Boolean(currentResolution)
  });
  return Object.assign(Object.assign({}, query), {
    data: portfolioChartDataLC[currentResolution.periodLabel] || query.data,
    isLoading: !portfolioChartDataLC[currentResolution.periodLabel] && query.isLoading
  });
};